// @ts-nocheck
/**
 * TwilioCallService — refactor 2026-06-04 (Phase 2 du big-bang).
 *
 * Centralise tous les appels Twilio cote client (hangup, hangup-all,
 * parallel dial). Avant : 15+ usages eparpilles de api.functions.invoke
 * dans DialerContext + useDialerSessionHandlers + useIncomingCallSession.
 * Apres : une seule API claire, un seul endroit a modifier si Twilio
 * change.
 *
 * Pourquoi pas un Singleton ? Pour pouvoir injecter un mock dans les
 * tests Playwright a venir (Chantier 5).
 */

import { api } from '@/api/api-client';
import { disconnectDeviceCalls } from '@/lib/twilioDevice';

/**
 * Coupe un appel Twilio par son CallSid (la leg child browser↔Twilio).
 * Idempotent : un call deja completed renvoie une erreur absorbee.
 */
export async function hangupCall(callSid: string): Promise<void> {
  if (!callSid) return;
  try {
    await api.functions.invoke('twilioHangupCall', { callSid });
  } catch (e: any) {
    // Absorbe : call peut deja etre completed
    console.warn('[TwilioCallService] hangupCall failed', callSid, e?.message);
  }
}

/**
 * Measurement only (2026-10-07): when call:bridged reached this browser and
 * when the contact card was painted after it, both on this browser's clock.
 * The server logs them beside its own timeline. Never throws.
 */
export function reportCardTiming(callId: string, receivedAt: number, at: number, stage: 'shown' | 'enriched' = 'shown'): void {
  if (!callId) return;
  api.functions.invoke('twilioCardTiming', { callId, receivedAt, shownAt: at, stage }).catch(() => undefined);
}

/**
 * Coupe TOUS les CallSid actifs de l'agent cote Twilio API.
 * Filet de securite quand le device.disconnect() browser ne propage
 * pas a la leg parent SIP (cf. bug #4 documented dans BUGS-OUVERTS.md).
 * Idempotent.
 */
export async function hangupAllMine(): Promise<void> {
  try {
    await api.functions.invoke('twilioHangupAllMine');
  } catch (e: any) {
    console.warn('[TwilioCallService] hangupAllMine failed', e?.message);
  }
}

/**
 * Lance N appels paralleles via le backend Twilio. Utilise par le
 * Power Mode (chantier 2 useOutboundDialSession).
 */
export async function parallelDial(opts: {
  prospects: any[];
  agentIdentity: string;
  fromNumber: string;
  voicemailMode?: string;
  voicemailAudioUrl?: string;
  liveCoachEnabled?: boolean;
}): Promise<any> {
  return api.functions.invoke('parallelDialProspectsPublic', opts);
}

/**
 * Liste les numeros Twilio actifs du tenant courant.
 */
export async function listTenantNumbers(): Promise<any> {
  return api.functions.invoke('listTenantNumbers');
}

/**
 * Coupe TOUS les appels Twilio cote SDK browser. A appeler en plus de
 * hangupAllMine quand on veut un cleanup complet (cas Stop ou inbound
 * accepted qui interrompt les outbound en cours).
 */
export function disconnectAllBrowser(): void {
  try {
    const dev = (window as any).__twilioDevice;
    disconnectDeviceCalls(dev);
  } catch (e: any) {
    console.warn('[TwilioCallService] disconnectAllBrowser failed', e?.message);
  }
}

/**
 * Cleanup complet : disconnect browser + hangupAllMine serveur. Couvre
 * les 2 niveaux (leg child + leg parent SIP). Idempotent.
 */
export async function fullHangup(): Promise<void> {
  disconnectAllBrowser();
  await hangupAllMine();
}
